import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNotification } from './NotificationManager';
import './LeadsList.css';

function LeadsList({ token, company, onUpdate }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, with-phone, with-email, new, processed, rejected
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const { showNotification } = useNotification();

  useEffect(() => {
    if (company) {
      loadLeads();
      const widgetConfig = company.widget_config || {};
      setNotificationsEnabled(Boolean(widgetConfig.lead_email_notifications_enabled));
      setRecipientEmail(widgetConfig.lead_email_notifications_recipient || '');
    }
  }, [company]);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `/api/v1/dashboard/${company.id}/leads`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeads(response.data);
    } catch (error) {
      console.error('Ошибка загрузки лидов:', error);
      showNotification(`Ошибка загрузки лидов: ${error.response?.data?.detail || error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (sessionId, newStatus, comment = '') => {
    try {
      await axios.put(
        `/api/v1/dashboard/${company.id}/leads/${sessionId}/status`,
        { status: newStatus, comment: comment },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Обновляем локально без перезагрузки
      setLeads(prev => prev.map(lead =>
        lead.session_id === sessionId
          ? { ...lead, lead_status: newStatus, lead_comment: comment }
          : lead
      ));

      showNotification(
        newStatus === 'processed' ? '✅ Лид отмечен как обработанный' :
        newStatus === 'rejected' ? '❌ Лид отмечен как отказ' :
        newStatus === 'spam' ? '🚫 Лид отмечен как спам' :
        '🆕 Статус сброшен',
        'success'
      );
    } catch (error) {
      showNotification(`Ошибка: ${error.response?.data?.detail || error.message}`, 'error');
    }
  };

  const handleStatusChange = (sessionId, newStatus) => {
    if (newStatus === 'rejected') {
      const comment = window.prompt('Укажите причину отказа (необязательно):');
      updateLeadStatus(sessionId, newStatus, comment || 'Без комментария');
    } else {
      updateLeadStatus(sessionId, newStatus);
    }
  };

  const filteredLeads = leads.filter((lead) => {
    if (filter === 'with-phone') return lead.lead_phone;
    if (filter === 'with-email') return lead.lead_email;
    if (filter === 'new') return (lead.lead_status || 'new') === 'new';
    if (filter === 'processed') return lead.lead_status === 'processed';
    if (filter === 'rejected') return lead.lead_status === 'rejected';
    return true;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  };

  const safePathname = (url) => {
    if (!url) return '';
    try {
      return new URL(url).pathname;
    } catch (e) {
      return url;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'processed':
        return <span className="status-badge-lead processed">Обработан</span>;
      case 'rejected':
        return <span className="status-badge-lead rejected">Отказ</span>;
      case 'spam':
        return <span className="status-badge-lead spam">Спам</span>;
      default:
        return <span className="status-badge-lead new">Новый</span>;
    }
  };

  const saveLeadNotificationSettings = async (nextEnabled = notificationsEnabled) => {
    const trimmedEmail = recipientEmail.trim();

    if (nextEnabled && !trimmedEmail) {
      showNotification('Укажите email, на который отправлять лиды', 'warning');
      return false;
    }

    if (nextEnabled && trimmedEmail) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(trimmedEmail)) {
        showNotification('Введите корректный email', 'warning');
        return false;
      }
    }

    setSavingSettings(true);
    try {
      const currentWidgetConfig = company.widget_config || {};
      const updatePayload = {
        widget_config: {
          ...currentWidgetConfig,
          lead_email_notifications_enabled: nextEnabled,
          lead_email_notifications_recipient: nextEnabled ? trimmedEmail : '',
        },
      };

      const response = await axios.put(
        `/api/v1/tenants/${company.id}`,
        updatePayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (onUpdate) onUpdate(response.data);

      setNotificationsEnabled(Boolean(response.data?.widget_config?.lead_email_notifications_enabled));
      setRecipientEmail(response.data?.widget_config?.lead_email_notifications_recipient || '');
      showNotification('Настройки отправки лидов сохранены', 'success');
      return true;
    } catch (error) {
      console.error('Ошибка сохранения настроек лидов:', error);
      showNotification(`Ошибка сохранения: ${error.response?.data?.detail || error.message}`, 'error');
      return false;
    } finally {
      setSavingSettings(false);
    }
  };

  const handleToggleChange = async () => {
    const nextEnabled = !notificationsEnabled;
    setNotificationsEnabled(nextEnabled);
    if (!nextEnabled) {
      const success = await saveLeadNotificationSettings(false);
      if (!success) setNotificationsEnabled(true);
    }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    await saveLeadNotificationSettings(notificationsEnabled);
  };

  const exportToExcel = () => {
    const delimiter = ';';
    const headers = ['Имя', 'Телефон', 'Email', 'Дата', 'Сообщений', 'Статус', 'Комментарий', 'Страница'];
    const statusLabels = { new: 'Новый', processed: 'Обработан', rejected: 'Отказ', spam: 'Спам' };

    const rows = filteredLeads.map(lead => [
      lead.lead_name || '',
      lead.lead_phone || '',
      lead.lead_email || '',
      formatDate(lead.first_message_at),
      lead.messages_count,
      statusLabels[lead.lead_status] || statusLabels[lead.lead_status || 'new'] || 'Новый',
      lead.lead_comment || '',
      lead.page_url || ''
    ]);

    const escapeCell = (cell) => {
      const str = String(cell);
      if (str.includes(delimiter) || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvRows = [
      headers.map(escapeCell).join(delimiter),
      ...rows.map(row => row.map(escapeCell).join(delimiter))
    ];

    const csvString = csvRows.join('\n');
    const blob = new Blob(['\uFEFF' + csvString], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `leads_${company.company_name}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="leads-list-loading">Загрузка лидов...</div>;
  }

  return (
    <div className="leads-list">
      <div className="lead-notifications-card">
        <div className="lead-notifications-main">
          <div>
            <h3>Отправка новых лидов на почту</h3>
            <p>
              Если клиент оставит номер телефона в чате, система отправит на указанный email
              его контакты и полный диалог с кнопками быстрой обработки.
            </p>
          </div>
          <button
            type="button"
            className={`ios-switch ${notificationsEnabled ? 'enabled' : ''}`}
            onClick={handleToggleChange}
            disabled={savingSettings}
            aria-pressed={notificationsEnabled}
          >
            <span className="ios-switch-thumb" />
          </button>
        </div>
        {notificationsEnabled && (
          <form className="lead-notifications-form" onSubmit={handleSettingsSubmit}>
            <div className="lead-notifications-field">
              <label htmlFor="leadNotificationEmail">Почта для уведомлений</label>
              <input
                id="leadNotificationEmail"
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="owner@example.com"
                autoComplete="email"
              />
            </div>
            <button type="submit" className="save-lead-settings-btn" disabled={savingSettings}>
              {savingSettings ? 'Сохранение...' : 'Сохранить'}
            </button>
          </form>
        )}
      </div>

      <div className="leads-header">
        <div className="header-left">
          <h2>Лиды ({filteredLeads.length})</h2>
          <div className="lead-filters">
            <button className={`filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>Все</button>
            <button className={`filter-btn ${filter === 'new' ? 'active' : ''}`} onClick={() => setFilter('new')}>Новые</button>
            <button className={`filter-btn ${filter === 'processed' ? 'active' : ''}`} onClick={() => setFilter('processed')}>Обработанные</button>
            <button className={`filter-btn ${filter === 'rejected' ? 'active' : ''}`} onClick={() => setFilter('rejected')}>Отказы</button>
            <button className={`filter-btn ${filter === 'with-phone' ? 'active' : ''}`} onClick={() => setFilter('with-phone')}>С телефоном</button>
            <button className={`filter-btn ${filter === 'with-email' ? 'active' : ''}`} onClick={() => setFilter('with-email')}>С email</button>
          </div>
        </div>
        <div className="header-right">
          <button onClick={exportToExcel} className="export-btn">Экспорт в Excel</button>
          <button onClick={loadLeads} className="refresh-btn">Обновить</button>
        </div>
      </div>

      <div className="leads-table-container">
        <table className="leads-table">
          <thead>
            <tr>
              <th>Имя</th>
              <th>Телефон</th>
              <th>Email</th>
              <th>Дата</th>
              <th>Сообщений</th>
              <th>Статус</th>
              <th>Действия</th>
              <th>Страница</th>
            </tr>
          </thead>
          <tbody>
            {filteredLeads.length === 0 ? (
              <tr><td colSpan="8" className="no-data">Нет лидов</td></tr>
            ) : (
              filteredLeads.map((lead) => (
                <tr key={lead.session_id}>
                  <td>
                    <div className="lead-name">{lead.lead_name || 'Не указано'}</div>
                  </td>
                  <td>
                    {lead.lead_phone ? (
                      <a href={`tel:${lead.lead_phone}`} className="phone-link">{lead.lead_phone}</a>
                    ) : '—'}
                  </td>
                  <td>
                    {lead.lead_email ? (
                      <a href={`mailto:${lead.lead_email}`} className="email-link">{lead.lead_email}</a>
                    ) : '—'}
                  </td>
                  <td>
                    <div>{formatDate(lead.first_message_at)}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>{formatDateTime(lead.lead_processed_at)}</div>
                  </td>
                  <td>{lead.messages_count}</td>
                  <td>{getStatusBadge(lead.lead_status || 'new')}</td>
                  <td>
                    <select
                      value={lead.lead_status || 'new'}
                      onChange={(e) => handleStatusChange(lead.session_id, e.target.value)}
                      className="status-select"
                      style={{
                        padding: '6px 10px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                        background: 'var(--bg-tertiary)',
                        color: 'var(--text-primary)',
                        fontSize: '13px',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="new">Новый</option>
                      <option value="processed">Обработан</option>
                      <option value="rejected">Отказ</option>
                      <option value="spam">Спам</option>
                    </select>
                    {lead.lead_comment && (
                      <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={lead.lead_comment}>
                        💬 {lead.lead_comment}
                      </div>
                    )}
                  </td>
                  <td>
                    {lead.page_url ? (
                      <a href={lead.page_url} target="_blank" rel="noopener noreferrer" className="page-link">
                        {safePathname(lead.page_url)}
                      </a>
                    ) : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="leads-stats">
        <div className="stat-item">
          <span className="stat-label">Всего лидов:</span>
          <span className="stat-value">{leads.length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Новых:</span>
          <span className="stat-value" style={{ color: '#3b82f6' }}>{leads.filter(l => (l.lead_status || 'new') === 'new').length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Обработано:</span>
          <span className="stat-value" style={{ color: '#10b981' }}>{leads.filter(l => l.lead_status === 'processed').length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Отказов:</span>
          <span className="stat-value" style={{ color: '#ef4444' }}>{leads.filter(l => l.lead_status === 'rejected').length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Спам:</span>
          <span className="stat-value" style={{ color: '#f59e0b' }}>{leads.filter(l => l.lead_status === 'spam').length}</span>
        </div>
      </div>
    </div>
  );
}

export default LeadsList;